import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrestationLibrary = ({ onSelectPrestation, filterByCategory }) => {
  const [prestations, setPrestations] = useState([]);
  const [groupedPrestations, setGroupedPrestations] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'travel_expenses',
    unit_price_ht: '',
    unit: 'unité',
    default_quantity: '1',
    detailed_description: '',
    regulatory_references: [],
    measurement_protocol: '',
    legal_context: '',
    client_explanation: '',
    technical_details: ''
  });

  const handleBack = () => {
    window.location.href = '/dashboard';
  };

  useEffect(() => {
    loadPrestations();
  }, []);

  const loadPrestations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/admin/prestations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPrestations(response.data.prestations || []);
      setGroupedPrestations(response.data.grouped_by_category || {});
    } catch (error) {
      console.error('Error loading prestations:', error);
      toast.error('Erreur lors du chargement des prestations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('tkale_token');
      const data = {
        ...form,
        unit_price_ht: parseFloat(form.unit_price_ht),
        default_quantity: parseFloat(form.default_quantity)
      };
      
      if (editingPrestation) {
        await axios.put(`${API}/admin/prestations/${editingPrestation.presta_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Prestation mise à jour');
      } else {
        await axios.post(`${API}/admin/prestations/create`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Prestation créée avec succès');
      }
      
      setShowCreateModal(false);
      setEditingPrestation(null);
      resetForm();
      loadPrestations();
    } catch (error) {
      console.error('Error saving prestation:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (prestaId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver cette prestation ?')) return;
    
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.delete(`${API}/admin/prestations/${prestaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Prestation désactivée');
      loadPrestations();
    } catch (error) {
      console.error('Error deleting prestation:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (prestation) => {
    setEditingPrestation(prestation);
    setForm({
      name: prestation.name,
      description: prestation.description,
      category: prestation.category,
      unit_price_ht: prestation.unit_price_ht.toString(),
      unit: prestation.unit,
      default_quantity: prestation.default_quantity.toString(),
      detailed_description: prestation.detailed_description || '',
      regulatory_references: prestation.regulatory_references || [],
      measurement_protocol: prestation.measurement_protocol || '',
      legal_context: prestation.legal_context || '',
      client_explanation: prestation.client_explanation || '',
      technical_details: prestation.technical_details || ''
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      category: 'travel_expenses',
      unit_price_ht: '',
      unit: 'unité',
      default_quantity: '1',
      detailed_description: '',
      regulatory_references: [],
      measurement_protocol: '',
      legal_context: '',
      client_explanation: '',
      technical_details: ''
    });
    setEditingPrestation(null);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'travel_expenses': 'Déplacements & Frais',
      'labor': 'Main d\'œuvre',
      'equipment': 'Matériel',
      'miscellaneous': 'Divers'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'travel_expenses': 'bg-blue-100 text-blue-800',
      'labor': 'bg-green-100 text-green-800',
      'equipment': 'bg-purple-100 text-purple-800',
      'miscellaneous': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['miscellaneous'];
  };

  return (
    <div className="space-y-6">
      {!onSelectPrestation && (
        <div className="flex items-center justify-between mb-4">
          <Button onClick={handleBack} variant="ghost" size="sm">
            <X className="w-4 h-4 mr-2" />
            Retour au Dashboard
          </Button>
        </div>
      )}
      
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Bibliothèque de Prestations</CardTitle>
            {!onSelectPrestation && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Prestation
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Chargement...</div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedPrestations).map(([category, items]) => {
                // Filter by category if specified
                if (filterByCategory && category !== filterByCategory) return null;
                
                if (items.length === 0) return null;
                
                return (
                  <div key={category}>
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Badge className={getCategoryColor(category)}>
                        {getCategoryLabel(category)}
                      </Badge>
                      <span className="ml-2 text-sm text-gray-500">({items.length})</span>
                    </h3>
                    <div className="space-y-2">
                      {items.map((presta) => (
                        <div key={presta.presta_id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{presta.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{presta.description}</p>
                              <div className="flex items-center space-x-4 mt-2 text-sm">
                                <span className="font-medium text-blue-600">
                                  {presta.unit_price_ht.toFixed(2)} € HT / {presta.unit}
                                </span>
                                <span className="text-gray-500">
                                  Quantité par défaut: {presta.default_quantity}
                                </span>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              {onSelectPrestation && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => onSelectPrestation(presta)}
                                >
                                  Ajouter
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEdit(presta)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDelete(presta.presta_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPrestation ? 'Modifier la Prestation' : 'Nouvelle Prestation'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nom de la prestation *</Label>
              <Input 
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Description courte *</Label>
              <Input 
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(value) => setForm({...form, category: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="travel_expenses">Déplacements & Frais</SelectItem>
                  <SelectItem value="labor">Main d'œuvre</SelectItem>
                  <SelectItem value="equipment">Matériel</SelectItem>
                  <SelectItem value="miscellaneous">Divers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Prix unitaire HT * (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={form.unit_price_ht}
                  onChange={(e) => setForm({...form, unit_price_ht: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Unité *</Label>
                <Select value={form.unit} onValueChange={(value) => setForm({...form, unit: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unité">Unité</SelectItem>
                    <SelectItem value="heure">Heure</SelectItem>
                    <SelectItem value="jour">Jour</SelectItem>
                    <SelectItem value="km">Kilomètre</SelectItem>
                    <SelectItem value="m²">Mètre carré</SelectItem>
                    <SelectItem value="forfait">Forfait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantité par défaut *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={form.default_quantity}
                  onChange={(e) => setForm({...form, default_quantity: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Description détaillée</Label>
              <Textarea 
                value={form.detailed_description}
                onChange={(e) => setForm({...form, detailed_description: e.target.value})}
                rows={3}
              />
            </div>

            {/* New Regulatory Fields */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-md mb-3">Informations Réglementaires</h3>
              
              <div>
                <Label>Protocole de mesure</Label>
                <Textarea 
                  value={form.measurement_protocol}
                  onChange={(e) => setForm({...form, measurement_protocol: e.target.value})}
                  placeholder="Ex: Mesure sur 15 minutes, sonomètre classe 1..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Contexte légal</Label>
                <Textarea 
                  value={form.legal_context}
                  onChange={(e) => setForm({...form, legal_context: e.target.value})}
                  placeholder="Ex: Article R1334-33 du Code de la Santé Publique..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Explication client (consultable in-app uniquement)</Label>
                <Textarea 
                  value={form.client_explanation}
                  onChange={(e) => setForm({...form, client_explanation: e.target.value})}
                  placeholder="Pourquoi cette prestation est nécessaire..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Détails techniques</Label>
                <Textarea 
                  value={form.technical_details}
                  onChange={(e) => setForm({...form, technical_details: e.target.value})}
                  placeholder="Détails techniques complémentaires..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Sauvegarde...' : editingPrestation ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestationLibrary;
